import { getAllGuides, getGuideBySlug } from "./loadGuides";
import { faqEntries } from "../../pages/help/content/faq";
import type { FaqEntry, Guide } from "./types";

function tagScore(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const setA = new Set(a);
  const intersection = b.filter((t) => setA.has(t)).length;
  if (!intersection) return 0;
  const union = new Set([...a, ...b]).size;
  return intersection / union;
}

interface Ranked<T> {
  item: T;
  score: number;
}

export function getRelatedGuides(slug: string, limit = 3): Guide[] {
  const current = getGuideBySlug(slug);
  if (!current) return [];
  const tags = current.frontmatter.tags;

  const ranked: Ranked<Guide>[] = getAllGuides()
    .filter((g) => g.frontmatter.slug !== slug)
    .map((g) => {
      const base = tagScore(tags, g.frontmatter.tags);
      const categoryBonus = g.frontmatter.category === current.frontmatter.category ? 0.01 : 0;
      return { item: g, score: base + categoryBonus };
    })
    .filter((r) => r.score > 0);

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.item.frontmatter.order - b.item.frontmatter.order;
  });

  return ranked.slice(0, limit).map((r) => r.item);
}

export function getRelatedFaqs(tags: string[], excludeId?: string, limit = 3): FaqEntry[] {
  const ranked: Ranked<FaqEntry>[] = faqEntries
    .filter((e) => e.id !== excludeId)
    .map((e) => ({ item: e, score: tagScore(tags, e.tags) }))
    .filter((r) => r.score > 0);

  ranked.sort((a, b) => b.score - a.score);
  return ranked.slice(0, limit).map((r) => r.item);
}

export function getRelatedGuidesForFaq(entry: FaqEntry, limit = 2): Guide[] {
  if (entry.relatedGuides && entry.relatedGuides.length) {
    const resolved: Guide[] = [];
    for (const slug of entry.relatedGuides) {
      const guide = getGuideBySlug(slug);
      if (guide) resolved.push(guide);
      if (resolved.length >= limit) break;
    }
    if (resolved.length) return resolved;
  }

  const ranked: Ranked<Guide>[] = getAllGuides()
    .map((g) => ({ item: g, score: tagScore(entry.tags, g.frontmatter.tags) }))
    .filter((r) => r.score > 0);

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.item.frontmatter.order - b.item.frontmatter.order;
  });

  return ranked.slice(0, limit).map((r) => r.item);
}
